import React, { useEffect, useState, useRef } from 'react';
import { useNavigationHandler } from '../utils/navigation';
import { fetchAllCaseStudies } from '../lib/sanity';
import { useSearchParams } from 'react-router-dom';
import './WorkTable.css';
import './ProjectList.css';

function WorkTable() {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const videoRefs = useRef({});
  const { handleNavClick } = useNavigationHandler();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const caseStudies = await fetchAllCaseStudies();
        setProjects(caseStudies);
        
        // Extract all unique tags, capitalize them, and reorder according to specified sequence
        const allUniqueTags = [...new Set(caseStudies.flatMap(project => project.tags || []))];
        
        // Define the desired tag order
        const tagOrder = [
          'Direction',
          'Web-Design', 
          'Web-GL',
          '3D',
          'Motion',
          'Physical',
          'App',
          'Branding',
          'Fashion'
        ];
        
        // Capitalize all tags and sort them according to the specified order
        const capitalizedTags = allUniqueTags.map(tag => 
          tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase()
        );
        
        // Sort tags: first by the specified order, then alphabetically for any remaining tags
        const sortedTags = capitalizedTags.sort((a, b) => {
          const aIndex = tagOrder.indexOf(a);
          const bIndex = tagOrder.indexOf(b);
          
          // If both tags are in the order list, sort by their position
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          
          // If only one tag is in the order list, prioritize it
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          
          // If neither tag is in the order list, sort alphabetically
          return a.localeCompare(b);
        });
        
        setAllTags(sortedTags);
        
        // Check for tag filter in URL
        const tagFilter = searchParams.get('tag');
        if (tagFilter) {
          // Find the capitalized version of the tag for display
          const capitalizedTagFilter = tagFilter.charAt(0).toUpperCase() + tagFilter.slice(1).toLowerCase();
          setActiveFilter(capitalizedTagFilter);
          const filtered = caseStudies.filter(project =>
            project.tags && project.tags.some(projectTag =>
              projectTag.toLowerCase() === tagFilter.toLowerCase()
            )
          );
          setFilteredProjects(filtered);
        } else {
          setFilteredProjects(caseStudies);
        }
      } catch (error) {
        console.error('Error loading case studies:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [searchParams]);

  const handleProjectClick = (slug) => {
    handleNavClick('case-study', slug);
  };

  const handleProjectHover = (projectId, isHovering) => {
    const video = videoRefs.current[projectId];
    if (video) {
      if (isHovering) {
        video.play().catch(() => {
          // Video autoplay prevented
        });
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  };

  const handleTagFilter = (tag) => {
    if (activeFilter === tag) {
      // Clear filter
      setIsFiltering(true);
      setTimeout(() => {
        setActiveFilter(null);
        setFilteredProjects(projects);
        setIsFiltering(false);
      }, 150);
    } else {
      // Apply filter
      setIsFiltering(true);
      setTimeout(() => {
        setActiveFilter(tag);
        const filtered = projects.filter(project => 
          project.tags && project.tags.some(projectTag => 
            projectTag.toLowerCase() === tag.toLowerCase()
          )
        );
        setFilteredProjects(filtered);
        setIsFiltering(false);
      }, 150);
    }
  };

  const clearAllFilters = () => {
    setIsFiltering(true);
    setTimeout(() => {
      setActiveFilter(null);
      setFilteredProjects(projects);
      setIsFiltering(false);
    }, 150);
  };

  if (loading) return <div className="work-table fade-out">Loading...</div>;
  
  if (projects.length === 0) {
    return (
      <div className="work-table">
        <div className="work-table-container">
          <div className="no-projects">
            <p>No projects found. Please add some projects in the Sanity Studio.</p>
          </div>
        </div>
      </div>
    );
  }

  if (filteredProjects.length === 0 && activeFilter) {
    return (
      <div className="work-table">
        <div className="work-table-container">
          <div className="filter-controls">
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              Clear Filters
            </button>
          </div>
          <div className="no-projects">
            <p>No projects found with the tag "{activeFilter}".</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="work-table fade-in">
      <div className="work-table-container">
        {activeFilter && (
          <div className="filter-controls">
            <div className="filter-info">
              <span className="active-filter">Filtering by: {activeFilter}</span>
              <span className="filter-count">({filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''})</span>
            </div>
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              Clear Filters
            </button>
          </div>
        )}
        
        <div className="work-table-header">
          {allTags.map((tag, index) => (
            <div 
              key={index} 
              className={`header-tag ${activeFilter === tag ? 'active' : ''}`}
              onClick={() => handleTagFilter(tag)}
            >
              {tag.replace(/-/g, '\u2011')}
            </div>
          ))}
        </div>
        
        <div className={`work-table-body ${isFiltering ? 'fade-out' : 'fade-in'}`}>
          {filteredProjects.map((project, index) => {
            const projectId = project.slug.current;
            const isDisabled = project.disabled;
            
            return (
              <div 
                key={`${projectId}-${activeFilter || 'all'}-${index}`}
                className={`work-table-row ${isDisabled ? 'work-table-row-disabled' : ''}`}
                onClick={() => !isDisabled && handleProjectClick(projectId)}
                onMouseEnter={() => !isDisabled && handleProjectHover(projectId, true)}
                onMouseLeave={() => !isDisabled && handleProjectHover(projectId, false)}
              >
                <div className="row-year">
                  <span className="year-text">{project.year}</span>
                </div>
                
                <div className="row-thumbnail">
                  <div className="thumbnail-container">
                    <img 
                      src={project.thumbnail || 'https://via.placeholder.com/160x120?text=No+Image'} 
                      alt={project.titleLineOne}
                      className={`thumbnail-image desktop-thumbnail ${isDisabled ? 'thumbnail-image-disabled' : ''}`}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/160x120?text=No+Image';
                      }}
                    />
                    <img 
                      src={project.mobileThumbnail || project.thumbnail || 'https://via.placeholder.com/160x120?text=No+Image'} 
                      alt={project.titleLineOne}
                      className={`thumbnail-image mobile-thumbnail ${isDisabled ? 'thumbnail-image-disabled' : ''}`}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/160x120?text=No+Image';
                      }}
                    />
                    {project.hoverMedia && project.hoverMedia.url && (
                      project.hoverMedia.type === 'video' ? (
                        <video
                          ref={el => videoRefs.current[projectId] = el}
                          src={project.hoverMedia.url}
                          className="thumbnail-hover-media"
                          muted
                          playsInline
                          loop
                        />
                      ) : project.hoverMedia.type === 'image' ? (
                        <img
                          src={project.hoverMedia.url}
                          alt={`${project.titleLineOne} hover`}
                          className="thumbnail-hover-media"
                        />
                      ) : null
                    )}
                  </div>
                </div>
                
                <div className="row-project">
                  <div className="project-info">
                    <div className="agency-name">{project.agency || 'Independent'}</div>
                    <div className="project-title">
                      <div className="title-line">
                        <h3 className="project-title-top">{project.titleLineOne}</h3>
                        <h3 className="project-title-bottom">{project.titleLineOne}</h3>
                      </div>
                      {project.titleLineTwo && (
                        <div className="title-line">
                          <h3 className="project-title-top">{project.titleLineTwo}</h3>
                          <h3 className="project-title-bottom">{project.titleLineTwo}</h3>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="row-tags">
                  <div className="tags-container">
                    {project.tags && project.tags.length > 0 ? (
                      project.tags.map((tag, index) => (
                        <span 
                          key={index} 
                          className="project-tag clickable"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTagFilter(tag);
                          }}
                        >
                          {tag.replace(/-/g, '\u2011')}
                        </span>
                      ))
                    ) : (
                      <span className="project-tag no-tags">No tags</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default WorkTable;
